import React, { useState } from "react";
import { Baby, AlertCircle, Gamepad2, Headphones, HeartHandshake, ArrowRight, CheckCircle2, Sparkles, Plus } from "lucide-react";
import { useLang } from "@/App";
import { Button } from "@/components/ui/button";

const SERVICE_ICONS = [Gamepad2, Headphones, HeartHandshake];

export default function KidsAudiology() {
  const { t } = useLang();
  const k = t.kids;
  const [openIdx, setOpenIdx] = useState(null);
  return (
    <section id="infantil" className="py-20 md:py-28 bg-white" data-testid="kids-section">
      <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-sky-100 text-sky-900 text-sm font-semibold">
            <Baby className="w-4 h-4" />
            {k.kicker}
          </div>
          <h2 className="mt-5 text-4xl md:text-5xl font-extrabold tracking-tight text-blue-950 leading-[1.1]">
            {k.title}
          </h2>
          <p className="mt-5 text-lg text-slate-700 leading-relaxed">{k.subtitle}</p>
        </div>

        {/* Warning signs */}
        <div className="mt-14 rounded-3xl border border-amber-200 bg-amber-50/60 p-7 md:p-10" data-testid="kids-signs">
          <div className="flex items-start gap-4">
            <div className="grid place-items-center w-12 h-12 rounded-2xl bg-amber-100 text-amber-700 shrink-0">
              <AlertCircle className="w-7 h-7" />
            </div>
            <div>
              <h3 className="text-2xl md:text-3xl font-bold text-amber-950 leading-tight">
                {k.signsTitle}
              </h3>
              <p className="mt-2 text-base text-amber-900/80 leading-relaxed">{k.signsIntro}</p>
            </div>
          </div>
          <div className="mt-8 grid md:grid-cols-2 gap-5">
            {k.signs.map((group, i) => (
              <div key={i} className="rounded-2xl bg-white p-6 border border-amber-100" data-testid={`kids-signs-group-${i}`}>
                <div className="text-sm font-bold uppercase tracking-widest text-amber-700">
                  {group.age}
                </div>
                <ul className="mt-4 space-y-3">
                  {group.items.map((it, j) => (
                    <li key={j} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 mt-1 text-amber-600 shrink-0" />
                      <span className="text-base text-slate-800 leading-relaxed">{it}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Public aid 0-26 highlighted block */}
        <div
          className="mt-12 rounded-3xl bg-gradient-to-br from-blue-900 via-blue-800 to-sky-700 text-white p-7 md:p-10 shadow-xl relative overflow-hidden"
          data-testid="kids-aid"
        >
          <div className="absolute -top-16 -right-16 w-72 h-72 bg-white/10 rounded-full blur-3xl" />
          <div className="relative">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-400 text-blue-950 text-xs font-extrabold uppercase tracking-wider">
              <Sparkles className="w-3.5 h-3.5" />
              Ayuda pública
            </div>
            <h3 className="mt-4 text-2xl md:text-3xl lg:text-4xl font-extrabold text-white leading-tight">
              {k.aidTitle}
            </h3>
            <p className="mt-3 text-base md:text-lg text-sky-50 leading-relaxed max-w-3xl">
              {k.aidIntro}
            </p>
            <ul className="mt-7 grid md:grid-cols-2 gap-x-8 gap-y-3 max-w-4xl">
              {k.aidItems.map((it, j) => (
                <li key={j} className="flex items-start gap-3" data-testid={`kids-aid-item-${j}`}>
                  <CheckCircle2 className="w-5 h-5 mt-0.5 text-amber-300 shrink-0" />
                  <span className="text-base text-sky-50 leading-relaxed">{it}</span>
                </li>
              ))}
            </ul>
            <p className="mt-7 text-sm md:text-base text-sky-100/90 italic max-w-3xl">
              {k.aidNote}
            </p>
            <div className="mt-7">
              <a
                href="#contacto"
                className="group inline-flex items-center gap-3 px-7 py-3.5 bg-white text-blue-900 font-bold rounded-xl shadow-lg hover:shadow-2xl transition-all hover:-translate-y-0.5"
                data-testid="kids-aid-cta"
              >
                {k.aidCta}
                <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
              </a>
            </div>
          </div>
        </div>

        {/* Services */}
        <div className="mt-16">
          <h3 className="text-3xl md:text-4xl font-extrabold tracking-tight text-blue-950">
            {k.servicesTitle}
          </h3>
          <div className="mt-8 grid md:grid-cols-3 gap-6">
            {k.services.map((s, i) => {
              const Icon = SERVICE_ICONS[i] || Gamepad2;
              const isOpen = openIdx === i;
              return (
                <article
                  key={i}
                  onClick={() => setOpenIdx(isOpen ? null : i)}
                  className="group cursor-pointer rounded-2xl border border-slate-200 bg-slate-50 p-7 hover:bg-white hover:shadow-lg transition-all"
                  data-testid={`kids-service-${i}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="grid place-items-center w-14 h-14 rounded-2xl bg-blue-900 text-white">
                      <Icon className="w-7 h-7" />
                    </div>
                    <Plus className={`w-5 h-5 text-slate-400 transition-transform ${isOpen ? "rotate-45" : "group-hover:rotate-45"}`} />
                  </div>
                  <h4 className="mt-6 text-xl md:text-2xl font-bold text-slate-900 leading-tight">
                    {s.title}
                  </h4>
                  <div className={`overflow-hidden transition-all duration-300 ${isOpen ? "max-h-60 opacity-100 mt-3" : "max-h-0 opacity-0 group-hover:max-h-60 group-hover:opacity-100 group-hover:mt-3"}`}>
                    <p className="text-base text-slate-600 leading-relaxed">{s.desc}</p>
                  </div>
                </article>
              );
            })}
          </div>
        </div>

        <div className="mt-12">
          <Button
            asChild
            className="h-14 px-7 bg-blue-900 hover:bg-blue-800 text-white text-lg font-semibold rounded-xl shadow-lg"
            data-testid="kids-cta"
          >
            <a href="#contacto" className="inline-flex items-center gap-2">
              {k.cta}
              <ArrowRight className="w-5 h-5" />
            </a>
          </Button>
        </div>
      </div>
    </section>
  );
}
